import * as designTokens from '@calm/evexia-design-tokens';
import {
    LEGACY_SEMANTICS_SRC,
    nest,
    resolve,
    SEMANTICS_SRC,
    type LegacyPath,
    type PathTree,
    type SemanticPath,
} from '@calm/evexia-design-tokens';
// eslint-disable-next-line no-restricted-imports
import { createTheme } from '@mui/material';
import { figtreeFontFaces, createFontFace } from './font';
import {
    buildGradient,
    darkPalette,
    lightPalette,
    resolveColor,
} from './palette';
import typographyVariants, { FONT_FAMILY } from './typography';

declare module '@mui/material/styles' {
    interface TypographyVariants {
        srOnly: React.CSSProperties;
        body1Demi: React.CSSProperties;
        buttonLarge: React.CSSProperties;
        buttonSmall: React.CSSProperties;
        avatarLetter: React.CSSProperties;
        inputLabel: React.CSSProperties;
        inputText: React.CSSProperties;
        helperText: React.CSSProperties;
        tooltip: React.CSSProperties;
    }

    // allow configuration using `createTheme`
    interface TypographyVariantsOptions {
        srOnly?: React.CSSProperties;
        body1Demi?: React.CSSProperties;
        buttonLarge?: React.CSSProperties;
        buttonSmall?: React.CSSProperties;
        avatarLetter?: React.CSSProperties;
        inputLabel?: React.CSSProperties;
        inputText?: React.CSSProperties;
        helperText?: React.CSSProperties;
        tooltip?: React.CSSProperties;
    }

    interface ZIndex {
        skipToMain: number;
        audioSrControls: number;
        mobilePromoDrawer: number;
        audioPlayerUpperActions: number;
        mediaControls: number;
        loadingDialog: number;
        cookieDialog: number;
    }

    // The designer's semantic color, spacing and radius read-path — see the
    // buildSemantics doc block below for what `Semantics` is and why the type
    // is derived from LegacyPath.
    interface Theme {
        semantics: Semantics;
        ds: Ds;
    }
    interface ThemeOptions {
        semantics?: Semantics;
        ds?: Ds;
    }
}

declare module '@mui/material/Typography' {
    interface TypographyPropsVariantOverrides {
        srOnly: true;
        body1Demi: true;
        buttonLarge: true;
        buttonSmall: true;
        avatarLetter: true;
        inputLabel: true;
        inputText: true;
        helperText: true;
        tooltip: true;
    }
}

declare module '@mui/material/IconButton' {
    interface IconButtonClasses {
        sizeXsmall: true;
        sizeLegacyMedium: true;
        sizeLargePlayerControl: true;
        sizePlayerControl: true;
    }
    interface IconButtonPropsSizeOverrides {
        xsmall: true;
        legacyMedium: true;
        largePlayerControl: true;
        playerControl: true;
    }
    interface IconButtonPropsVariantOverrides {
        contained: true;
    }
    interface IconButtonOwnProps {
        variant?: 'contained';
    }
}

// Utilities to create real theme with
const defaultTheme = createTheme({
    spacing: designTokens.PRIMITIVES.spacing._4,
    breakpoints: {
        values: {
            xs: designTokens.DS_BREAKPOINT.INTRA,
            sm: designTokens.DS_BREAKPOINT.SMALL,
            md: designTokens.DS_BREAKPOINT.MEDIUM,
            lg: designTokens.DS_BREAKPOINT.LARGE,
            xl: designTokens.DS_BREAKPOINT.ULTRA,
        },
    },
});

export type ColorMode = 'light' | 'dark';

// --- theme.semantics: the designer's semantic color, spacing and radius read-path
// A mode-resolved map of the designer's semantic roles, read as
// `theme.semantics.Text.Heading`. Pre-flip its source is the LEGACY_SEMANTICS
// bridge (byte-identical to today's values); at the rebrand flip the bridge
// swaps to SEMANTICS in one place and consumers are untouched.
//
// The TYPE (PathTree<LegacyPath>) is derived from LegacyPath so reading a role
// with no bridge twin is a compile error (the "no role silently dropped"
// guarantee) — not a loose index signature, which would admit any key. The path
// projection (nest + the PathTree type) lives in the design-tokens bridge; only
// the null-tolerant resolution below is web-theme-specific. The Theme /
// ThemeOptions augmentation that surfaces this on the theme lives in the
// `@mui/material/styles` block at the top of this file, alongside the other
// MUI augmentations.
//
// Spacing and corner radius have no bridge twin, so pre-flip they are picked
// out of SEMANTICS_SRC by prefix. SEMANTICS_SRC can't be surfaced whole yet:
// its color roles are the new brand (most with no bridge twin), and effects/*
// has no agreed web composition. At the flip, replace both halves of
// buildSemantics with one resolve() pass over SEMANTICS_SRC (minus any family
// still kept off the theme), and drop the prefix filter. NumberLeaves stays
// only while PathTree types every leaf as a string.
type SpacingOrRadiusPath = Extract<
    SemanticPath,
    `spacing/${string}` | `corner radius/${string}`
>;

type NumberLeaves<T> = {
    [K in keyof T]: T[K] extends string ? number : NumberLeaves<T[K]>;
};

type Semantics = PathTree<LegacyPath> &
    NumberLeaves<PathTree<SpacingOrRadiusPath>>;

const SPACING_OR_RADIUS_PATHS = (
    Object.keys(SEMANTICS_SRC) as SemanticPath[]
).filter(
    (path): path is SpacingOrRadiusPath =>
        path.startsWith('spacing/') || path.startsWith('corner radius/')
);

// A nested PascalCase tree: color roles as today's-value strings, resolved at
// `colorMode` via the null-tolerant resolveColor (DD2); spacing and corner
// radius as px numbers from the library's resolve(), which rejects a null mode
// (the values are the same in both modes). Projected once with the bridge's
// shared `nest`; color roles are built from the bridge's own keys, so twins
// added to LEGACY_SEMANTICS_SRC flow in with no edit here.
const buildSemantics = (colorMode: ColorMode): Semantics =>
    nest<string | number>({
        ...Object.fromEntries(
            (Object.keys(LEGACY_SEMANTICS_SRC) as LegacyPath[]).map((path) => [
                path,
                resolveColor(path, colorMode),
            ])
        ),
        ...Object.fromEntries(
            SPACING_OR_RADIUS_PATHS.map((path) => [
                path,
                resolve(path, colorMode === 'light' ? 'light' : 'dark'),
            ])
        ),
    }) as Semantics;

const DS = {
    // MUI Typography variants are the better long-term read path for text
    // styles; whether Figma gets a typography semantics group is still open.
    typography: designTokens.PRIMITIVES.typography,
    spacingUnits: designTokens.DS_SPACING_UNITS,
    height: designTokens.DS_HEIGHT,
    width: designTokens.DS_WIDTH,
    fontSize: designTokens.DS_FONT_SIZE,
};

type Ds = typeof DS;

// MUI applies 9px top/bottom padding to its `labelIcon` tabs (icon + label).
// Icon-only tabs don't qualify, so we restore the same value to keep a 24px
// icon from overflowing the tab height. Coupled to MUI internals — grep here
// if a future MUI version changes its labelIcon padding.
const MUI_LABEL_ICON_PADDING_PX = 9;

// Dark hover tint for text & outlined buttons. No design-system token matches a
// per-ramp 8% tint, so these stay raw; see SEMANTIC-WIRING.md (hover/press).
const DARK_TEXT_OUTLINED_HOVER_TINT = {
    primary: designTokens.DS_COLOR.DARK_PRIMARY_8P,
    secondary: designTokens.DS_COLOR.DARK_SECONDARY_8P,
    success: designTokens.DS_COLOR.DARK_SUCCESS_8P,
    info: designTokens.DS_COLOR.DARK_INFO_8P,
    warning: designTokens.DS_COLOR.DARK_WARNING_8P,
    error: designTokens.DS_COLOR.DARK_ERROR_8P,
} as const;

const focusedOutline = (colorMode: ColorMode) =>
    `${resolveColor('border/interactive-focus', colorMode)} auto 1px`;

// MuiRadio/MuiCheckbox/MuiSwitch all render their real focusable node as a
// visually-hidden <input> under a SwitchBase-derived root; the visible
// thumb/icon is its literal next sibling — a <span> for Radio/Switch, but an
// <svg> for Checkbox (MUI's default check icons are SvgIcon-based), and a
// consumer-supplied custom `icon` prop could be either. Match any element
// type so the three controls can't silently drift out of sync on this
// focus-ring pattern.
const styleHiddenInputFocusRing = (colorMode: ColorMode) => ({
    '& input:focus-visible+*': {
        outline: focusedOutline(colorMode),
    },
});

const makeTheme = (
    requiredLabelText?: string,
    colorMode: ColorMode = 'light'
) => {
    const theme = createTheme({
        components: {
            MuiChip: {
                defaultProps: {
                    size: 'small',
                },
                styleOverrides: {
                    // Figtree's ascent (~12px @13) dwarfs its descent (~3px),
                    // so the browser's metric-based line-box centering leaves
                    // the visible glyphs riding ~1px above where Figma places
                    // the chip label. Nudge the label (only — icons/delete stay
                    // centered) down 1px so the text lands on the Figma optical
                    // center. See EVE-12334.
                    label: {
                        ...typographyVariants.chip,
                        position: 'relative',
                        top: '1px',
                    },
                },
            },
            MuiListItemText: {
                styleOverrides: {
                    primary: ({ theme }) => ({
                        color: theme.palette.text.body,
                    }),
                    secondary: ({ theme }) => ({
                        color: theme.palette.text.sub,
                    }),
                },
            },
            MuiListItemButton: {
                styleOverrides: {
                    root: {
                        color: resolveColor('icon/default', colorMode),
                    },
                },
            },
            MuiLinearProgress: {
                styleOverrides: {
                    root: {
                        backgroundColor: resolveColor(
                            'surface/progress/track',
                            colorMode
                        ),
                    },
                },
            },
            MuiFormLabel: {
                styleOverrides: {
                    asterisk: {
                        // hiding the asterisk
                        fontSize: 0,
                        '&::after': {
                            marginLeft: defaultTheme.spacing(0.5),
                            content: requiredLabelText,
                            // restoring font size so that this shows
                            fontSize: '1rem',
                        },
                    },
                },
            },
            MuiButton: {
                defaultProps: {
                    disableElevation: true,
                    size: 'large',
                },
                styleOverrides: {
                    sizeLarge: {
                        height: designTokens.DS_HEIGHT.BUTTON_LARGE,
                        borderRadius: designTokens.PRIMITIVES.radius._24,
                        fontSize: designTokens.DS_FONT_SIZE.BUTTON_LARGE,
                        lineHeight: designTokens.DS_LINE_HEIGHT.BUTTON_LARGE,
                        letterSpacing: designTokens.DS_LETTER_SPACING.L6,
                    },
                    sizeMedium: {
                        height: designTokens.DS_HEIGHT.BUTTON_MEDIUM,
                        borderRadius:
                            designTokens.DS_SHAPE_BORDER_RADIUS.BUTTON_MEDIUM,
                        fontSize: designTokens.PRIMITIVES.typography.size._14,
                        lineHeight: `${designTokens.PRIMITIVES.typography.lineHeight._24}px`,
                        letterSpacing: designTokens.DS_LETTER_SPACING.L5,
                    },
                    sizeSmall: {
                        height: designTokens.DS_HEIGHT.BUTTON_SMALL,
                        borderRadius: designTokens.PRIMITIVES.radius._16,
                        fontSize: designTokens.PRIMITIVES.typography.size._13,
                        lineHeight: `${designTokens.PRIMITIVES.typography.lineHeight._22}px`,
                        letterSpacing: designTokens.DS_LETTER_SPACING.L6,
                    },
                },
                // Dark-only: text & outlined hover uses the per-color
                // */Shades/8p brand tint instead of MUI's default
                // alpha(palette[color].main, hoverOpacity), which collapses to
                // white@8p in dark because dark primary.main = #FFFFFF. Light is
                // correct as-is (4p) and intentionally untouched.
                variants:
                    colorMode === 'dark'
                        ? (
                              [
                                  'primary',
                                  'secondary',
                                  'success',
                                  'info',
                                  'warning',
                                  'error',
                              ] as const
                          ).flatMap((color) =>
                              (['text', 'outlined'] as const).map(
                                  (variant) => ({
                                      props: { variant, color },
                                      style: {
                                          '&:hover': {
                                              backgroundColor:
                                                  DARK_TEXT_OUTLINED_HOVER_TINT[
                                                      color
                                                  ],
                                              // Match MUI: no hover bg on touch devices.
                                              '@media (hover: none)': {
                                                  backgroundColor:
                                                      'transparent',
                                              },
                                          },
                                      },
                                  })
                              )
                          )
                        : [],
            },
            MuiFab: {
                styleOverrides: {
                    // Circular FABs must be true circles at every size (Figma:
                    // 40/48/56). The previous override applied button
                    // height/radius tokens (and no width), leaking onto MUI's
                    // per-size width + minHeight:36 defaults and squashing the
                    // small Fab to 40×36 with a 16px radius. Both the 50%
                    // radius and the FAB box geometry (DS_HEIGHT.FAB_* — a
                    // dedicated scale, not the button heights that caused the
                    // squash) are scoped to the `circular` variant so the
                    // Extended (pill) variant keeps its stadium radius and
                    // `width: auto` at every size.
                    // fontSize tokens stay on the size keys (shared by both
                    // variants) — typography is out of scope.
                    circular: {
                        borderRadius: '50%',
                        '&.MuiFab-sizeSmall': {
                            width: designTokens.DS_HEIGHT.FAB_SMALL,
                            height: designTokens.DS_HEIGHT.FAB_SMALL,
                        },
                        '&.MuiFab-sizeMedium': {
                            width: designTokens.DS_HEIGHT.FAB_MEDIUM,
                            height: designTokens.DS_HEIGHT.FAB_MEDIUM,
                        },
                    },
                    // Large Extended is MUI's default size and had no sizeLarge
                    // rule, so it fell through to MUI's 48px height + 14px label.
                    // Figma node 6556:38265 documents 50px / 15px. The 15px label
                    // mirrors sizeMedium/sizeSmall; it lives on a root-scoped
                    // `&.MuiFab-sizeLarge` selector rather than a top-level
                    // `sizeLarge` key because MUI's FabClasses type omits
                    // `sizeLarge` (the class exists at runtime, but the key fails
                    // tsc). Height is scoped to the extended variant so circular
                    // Large stays a 56px circle. The fontSize below sits on
                    // `root`, so it also reaches circular Large — harmless
                    // (SvgIcon sizes via its own typography context, not parent
                    // `font-size`) and consistent with how sizeMedium/sizeSmall
                    // set fontSize for both variants.
                    root: {
                        '&.MuiFab-sizeLarge': {
                            fontSize: designTokens.DS_FONT_SIZE.BUTTON_LARGE,
                        },
                    },
                    extended: {
                        '&.MuiFab-sizeLarge': {
                            height: designTokens.DS_HEIGHT.FAB_EXTENDED_LARGE,
                        },
                    },
                    sizeMedium: {
                        fontSize: designTokens.DS_FONT_SIZE.BUTTON_LARGE,
                    },
                    sizeSmall: {
                        fontSize: designTokens.PRIMITIVES.typography.size._13,
                    },
                },
            },
            MuiIconButton: {
                styleOverrides: {
                    // IconButton size scale — box + icon sizes are
                    // token-driven (ICON_BUTTON_* height + icon-font-size
                    // tokens), with padding = (box - icon) / 2 expressed in
                    // PRIMITIVES.spacing._4 (4px) units. Exception: xsmall's
                    // box reuses the shared BUTTON_XSMALL height token (24px);
                    // only its icon uses an ICON_BUTTON_* token.
                    //
                    // Sanctioned (Figma) sizes: small 32/24, medium (default)
                    // 48/24, large 64/40. Off-Figma sizes kept for pixel-
                    // preserving callsites and flagged for design review:
                    // xsmall 24/16, legacyMedium 40/24.
                    //
                    // legacyMedium intentionally sets NO width/height: the
                    // geometry it preserves (MUI's default medium) was
                    // content-hugging, and several callsites override its
                    // padding via `sx` (p: 0..3) to get boxes from 24px up.
                    // A fixed 40px box would silently resize all of those.
                    // With the default 8px padding and a 24px icon it still
                    // renders 40x40.
                    //
                    // MUI `SvgIcon` (and Material icons) render at `1em`, so
                    // `fontSize` drives icon size. The large icon sizes also
                    // pin `& svg { width, height }` as a fallback for raw SVGs
                    // with hard-coded dimensions that ignore `fontSize`.
                    sizeSmall: {
                        width: designTokens.DS_HEIGHT.ICON_BUTTON_SMALL,
                        height: designTokens.DS_HEIGHT.ICON_BUTTON_SMALL,
                        fontSize: designTokens.PRIMITIVES.typography.size._24,
                        padding: designTokens.PRIMITIVES.spacing._4, // 4 + 24 + 4 = 32
                    },
                    sizeMedium: {
                        width: designTokens.DS_HEIGHT.ICON_BUTTON_MEDIUM,
                        height: designTokens.DS_HEIGHT.ICON_BUTTON_MEDIUM,
                        fontSize: designTokens.PRIMITIVES.typography.size._24,
                        padding: designTokens.PRIMITIVES.spacing._4 * 3, // 12 + 24 + 12 = 48
                    },
                    sizeLarge: {
                        width: designTokens.DS_HEIGHT.ICON_BUTTON_LARGE,
                        height: designTokens.DS_HEIGHT.ICON_BUTTON_LARGE,
                        fontSize: designTokens.DS_FONT_SIZE.ICON_BUTTON_LARGE,
                        '& svg': {
                            width: designTokens.DS_FONT_SIZE.ICON_BUTTON_LARGE,
                            height: designTokens.DS_FONT_SIZE.ICON_BUTTON_LARGE,
                        },
                        padding: designTokens.PRIMITIVES.spacing._4 * 3, // 12 + 40 + 12 = 64
                    },
                    sizeXsmall: {
                        width: designTokens.DS_HEIGHT.BUTTON_XSMALL,
                        height: designTokens.DS_HEIGHT.BUTTON_XSMALL,
                        fontSize: designTokens.PRIMITIVES.typography.size._16,
                        padding: designTokens.PRIMITIVES.spacing._4, // 4 + 16 + 4 = 24
                    },
                    sizeLegacyMedium: {
                        fontSize: designTokens.PRIMITIVES.typography.size._24,
                        padding: designTokens.PRIMITIVES.spacing._4 * 2, // 8 + 24 + 8 = 40
                    },
                    sizeLargePlayerControl: {
                        width: designTokens.DS_HEIGHT
                            .ICON_BUTTON_LARGE_PLAYER_CONTROL,
                        height: designTokens.DS_HEIGHT
                            .ICON_BUTTON_LARGE_PLAYER_CONTROL,
                        fontSize:
                            designTokens.DS_FONT_SIZE
                                .ICON_BUTTON_LARGE_PLAYER_CONTROL,
                        '& svg': {
                            width: designTokens.DS_FONT_SIZE
                                .ICON_BUTTON_LARGE_PLAYER_CONTROL,
                            height: designTokens.DS_FONT_SIZE
                                .ICON_BUTTON_LARGE_PLAYER_CONTROL,
                        },
                        padding:
                            (designTokens.DS_HEIGHT
                                .ICON_BUTTON_LARGE_PLAYER_CONTROL -
                                designTokens.DS_FONT_SIZE
                                    .ICON_BUTTON_LARGE_PLAYER_CONTROL) /
                            2, // (56 - 39) / 2 = 8.5
                    },
                    // Same 48px box as sizeMedium - icon grows to 32px
                    sizePlayerControl: {
                        width: designTokens.DS_HEIGHT.ICON_BUTTON_MEDIUM,
                        height: designTokens.DS_HEIGHT.ICON_BUTTON_MEDIUM,
                        fontSize:
                            designTokens.DS_FONT_SIZE
                                .ICON_BUTTON_PLAYER_CONTROL,
                        '& svg': {
                            width: designTokens.DS_FONT_SIZE
                                .ICON_BUTTON_PLAYER_CONTROL,
                            height: designTokens.DS_FONT_SIZE
                                .ICON_BUTTON_PLAYER_CONTROL,
                        },
                        padding: designTokens.PRIMITIVES.spacing._4 * 2, // 8 + 32 + 8 = 48
                    },
                },
                variants: [
                    {
                        props: { variant: 'contained' },
                        style: ({ theme }) => ({
                            borderRadius: '50%',
                            backgroundColor:
                                theme.palette.mode === 'light'
                                    ? theme.palette.background.paper
                                    : theme.palette.mask.black['60p'],
                        }),
                    },
                ],
            },
            MuiAutocomplete: {
                styleOverrides: {
                    // Autocomplete renders its indicators as internal
                    // IconButtons with no `size`, so they pick up the
                    // sizeMedium fixed 48px box above and blow out the input
                    // layout. Unset the box so they content-hug as they did
                    // before the IconButton size scale was token-aligned
                    // (Autocomplete's own slot padding still applies).
                    clearIndicator: {
                        width: 'auto',
                        height: 'auto',
                    },
                    popupIndicator: {
                        width: 'auto',
                        height: 'auto',
                    },
                },
            },
            MuiTextField: {
                defaultProps: {
                    variant: 'filled',
                },
            },
            MuiFilledInput: {
                styleOverrides: {
                    input: {
                        borderRadius: designTokens.PRIMITIVES.radius._8,
                    },
                    root: ({ theme }) => ({
                        borderRadius: designTokens.PRIMITIVES.radius._8,
                        backgroundColor: resolveColor(
                            'surface/input',
                            colorMode
                        ),
                        // Render the resting 1px border as an inset box-shadow
                        // so it does not participate in layout (matching Figma's
                        // inside stroke). A real `border` on this auto-height root
                        // adds 1px top + bottom, inflating the outer height to
                        // 50/58 instead of Figma's 48/56.
                        boxShadow: `inset 0 0 0 1px ${theme.palette.inputLine}`,
                        // box-shadow is dropped in forced-colors / Windows High
                        // Contrast mode, so restore a visible resting boundary
                        // there (WCAG 1.4.11). Inherited by the error/disabled
                        // states below (they only recolor the box-shadow); the
                        // focused state overrides it with its own 2px outline.
                        '@media (forced-colors: active)': {
                            outline: '1px solid',
                            outlineOffset: '-1px',
                        },
                        color: theme.palette.text.body,
                        // Same inside-stroke trick as `root` above: render the
                        // error border as an inset box-shadow so it does not add
                        // to the box.
                        '&.Mui-error': {
                            boxShadow: `inset 0 0 0 1px ${resolveColor(
                                'border/error',
                                colorMode
                            )}`,
                        },
                        // Same inside-stroke trick as `root` above.
                        '&.Mui-disabled': {
                            boxShadow: `inset 0 0 0 1px ${resolveColor(
                                'border/disabled',
                                colorMode
                            )}`,
                        },
                        // The global MuiInputBase `input:focus-visible` outline
                        // (and the browser default on <textarea>) would
                        // otherwise stack on top of the box-shadow focus ring
                        // below; suppress it so the ring is the sole indicator
                        // (the forced-colors fallback in `&.Mui-focused` covers
                        // high-contrast mode). Target the .MuiFilledInput-input
                        // class — carried by both the single-line <input> and
                        // the multiline <textarea> — rather than the `input`
                        // element, so multiline filled fields are covered too.
                        '& .MuiFilledInput-input:focus-visible': {
                            outline: 0,
                        },
                        '&.Mui-focused': {
                            // Input background does not change on focus in the
                            // new DS; the honest role is surface/input, but its
                            // twin is claimed by the resting value (dark
                            // #00000033 ≠ this opaque #1A1A2C), so wiring it here
                            // would move pixels. Stays raw; the one-line wire
                            // rides the rebrand flip PR. See SEMANTIC-WIRING.md.
                            backgroundColor:
                                colorMode === 'light'
                                    ? designTokens.DS_COLOR
                                          .LIGHT_BACKGROUND_PAPER
                                    : designTokens.DS_COLOR
                                          .DARK_BACKGROUND_PAPER,
                            boxShadow: `inset 0 0 0 2px ${theme.palette.primary.main}`,
                            // box-shadow is dropped in forced-colors / Windows
                            // High Contrast mode, so restore a visible focus
                            // indicator there (WCAG 2.4.7 / 2.4.11).
                            '@media (forced-colors: active)': {
                                outline: '2px solid',
                                outlineOffset: '-2px',
                            },
                            '&.Mui-error': {
                                boxShadow: `inset 0 0 0 2px ${resolveColor(
                                    'border/error',
                                    colorMode
                                )}`,
                            },
                        },
                        // Get rid of underline
                        '&:before': {
                            border: '0 !important',
                        },
                        '&:after': {
                            border: '0 !important',
                        },
                    }),
                },
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        top: '2px',
                    },
                    filled: ({ theme }) => ({
                        color: theme.palette.text.body,
                        '&.Mui-focused:not(.Mui-error)': {
                            color: theme.palette.text.body,
                        },
                    }),
                    shrink: ({ theme }) => ({
                        top: '0px',
                        color: theme.palette.text.body,
                    }),
                },
            },
            MuiButtonBase: {
                defaultProps: {
                    disableRipple: true,
                },
                styleOverrides: {
                    root: {
                        '&:focus-visible': {
                            // makes it easy to see which input is focused when using keyboard navigation
                            outline: focusedOutline(colorMode),
                        },
                    },
                },
            },
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        boxShadow: 'none',
                    },
                },
            },
            MuiDialogActions: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        borderTop:
                            colorMode === 'light'
                                ? `1px solid ${theme.palette.divider}`
                                : 'none',
                    }),
                },
            },
            MuiToolbar: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        boxShadow: 'none',
                        justifyContent: 'space-between',
                        borderBottom:
                            colorMode === 'light'
                                ? `1px solid ${theme.palette.divider}`
                                : 'none',
                        // Dark arm has no toolbar/app-bar surface role (gradient
                        // start anchor); stays raw. See SEMANTIC-WIRING.md.
                        backgroundColor:
                            colorMode === 'light'
                                ? theme.palette.background.default
                                : designTokens.DS_COLOR
                                      .DARK_BACKGROUND_PRIMARY_START,
                        [`${defaultTheme.breakpoints.down(
                            'sm'
                        )} and (orientation: landscape)`]: {
                            minHeight: designTokens.DS_HEIGHT.TOOLBAR_MOBILE,
                        },
                        [defaultTheme.breakpoints.down('md')]: {
                            paddingLeft: designTokens.PRIMITIVES.spacing._16,
                            paddingRight: designTokens.PRIMITIVES.spacing._16,
                        },
                        [defaultTheme.breakpoints.between('md', 'lg')]: {
                            paddingLeft: designTokens.PRIMITIVES.spacing._24,
                            paddingRight: designTokens.PRIMITIVES.spacing._24,
                        },
                        [defaultTheme.breakpoints.up('lg')]: {
                            paddingLeft: designTokens.PRIMITIVES.spacing._32,
                            paddingRight: designTokens.PRIMITIVES.spacing._32,
                        },
                    }),
                },
            },
            MuiDrawer: {
                styleOverrides: {
                    paper: {
                        background:
                            colorMode === 'light'
                                ? undefined
                                : buildGradient('background/primary', 'dark'),
                    },
                },
            },
            MuiInputBase: {
                styleOverrides: {
                    input: {
                        '&:focus-visible': {
                            // makes it easy to see which input is focused when using keyboard navigation
                            outline: focusedOutline(colorMode),
                        },
                    },
                },
            },
            MuiRadio: {
                styleOverrides: {
                    root: {
                        ...styleHiddenInputFocusRing(colorMode),
                        // Figma DS canon: a checked control is interactive/
                        // secondary, but that role's bridge twin is claimed by
                        // secondary.main's (different) today-value, so wiring
                        // now would move pixels. Stays raw; the one-line wire
                        // to theme.palette.secondary.main rides the rebrand
                        // flip PR. See SEMANTIC-WIRING.md.
                        '&.MuiRadio-colorDefault.Mui-checked': {
                            color:
                                colorMode === 'light'
                                    ? designTokens.DS_COLOR.LIGHT_SECONDARY_DARK
                                    : designTokens.DS_COLOR.DARK_SECONDARY_DARK,
                        },
                        // MUI gives every palette color a disabled override but
                        // leaves `default` blank, so the checked override above
                        // would otherwise bleed into the disabled state.
                        // Figma DS canon: disabled controls are text/disabled,
                        // whose twin is claimed by text.disabled's (different)
                        // today-value — same outlier rule as above; the wire to
                        // theme.palette.text.disabled rides the flip PR. See
                        // SEMANTIC-WIRING.md.
                        '&.MuiRadio-colorDefault.Mui-disabled': {
                            color:
                                colorMode === 'light'
                                    ? designTokens.DS_COLOR
                                          .LIGHT_ACTION_DISABLED
                                    : designTokens.DS_COLOR
                                          .DARK_ACTION_DISABLED,
                        },
                    },
                },
            },
            MuiCheckbox: {
                styleOverrides: {
                    root: {
                        ...styleHiddenInputFocusRing(colorMode),
                        // Figma DS canon: checked = interactive/secondary,
                        // disabled = text/disabled — both twins are claimed by
                        // other today-values, so these stay raw at P1 and their
                        // one-line wires ride the rebrand flip PR. See the
                        // MuiRadio block above and SEMANTIC-WIRING.md.
                        '&.MuiCheckbox-colorDefault.Mui-checked': {
                            color:
                                colorMode === 'light'
                                    ? designTokens.DS_COLOR.LIGHT_SECONDARY_DARK
                                    : designTokens.DS_COLOR.DARK_SECONDARY_DARK,
                        },
                        // MUI gives every palette color a disabled override but
                        // leaves `default` blank, so the checked override above
                        // would otherwise bleed into the disabled state.
                        '&.MuiCheckbox-colorDefault.Mui-disabled': {
                            color:
                                colorMode === 'light'
                                    ? designTokens.DS_COLOR
                                          .LIGHT_ACTION_DISABLED
                                    : designTokens.DS_COLOR
                                          .DARK_ACTION_DISABLED,
                        },
                    },
                },
            },
            MuiSwitch: {
                styleOverrides: {
                    switchBase: styleHiddenInputFocusRing(colorMode),
                },
            },
            MuiFormHelperText: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        ...typographyVariants.helperText,
                        color: theme.palette.text.sub,
                    }),
                },
            },
            MuiAvatar: {
                styleOverrides: {
                    root: {
                        fontSize: designTokens.PRIMITIVES.typography.size._20,
                        width: designTokens.DS_HEIGHT.AVATAR_SMALL,
                        height: designTokens.DS_HEIGHT.AVATAR_SMALL,
                        backgroundColor:
                            colorMode === 'light'
                                ? designTokens.DS_COLOR.GREY_400
                                : designTokens.DS_COLOR.GREY_600,
                    },
                    rounded: {
                        borderRadius: designTokens.PRIMITIVES.radius._4,
                    },
                },
            },
            MuiAlertTitle: {
                styleOverrides: {
                    root: {
                        // DS Components/Alert Title is SemiBold (600); MUI
                        // defaults AlertTitle to fontWeightMedium (500).
                        fontWeight:
                            designTokens.PRIMITIVES.typography.weight._600,
                    },
                },
            },
            MuiAlert: {
                styleOverrides: {
                    message: {
                        // The filled variant sets the root to fontWeightMedium
                        // (500), so its message renders 500; DS Body 2 is
                        // SemiBold (600). Outlined/standard already inherit 600
                        // from the body2 typography, so this is a no-op there.
                        fontWeight:
                            designTokens.PRIMITIVES.typography.weight._600,
                    },
                    standardError: {
                        color: resolveColor(
                            'text/on-feedback/error-subtle',
                            colorMode
                        ),
                    },
                    outlinedError: {
                        color: resolveColor(
                            'text/on-feedback/error-subtle',
                            colorMode
                        ),
                    },
                    standardWarning: {
                        color: resolveColor(
                            'text/on-feedback/warning-subtle',
                            colorMode
                        ),
                    },
                    outlinedWarning: {
                        color: resolveColor(
                            'text/on-feedback/warning-subtle',
                            colorMode
                        ),
                    },
                    standardSuccess: {
                        color: resolveColor(
                            'text/on-feedback/success-subtle',
                            colorMode
                        ),
                    },
                    outlinedSuccess: {
                        color: resolveColor(
                            'text/on-feedback/success-subtle',
                            colorMode
                        ),
                    },
                    standardInfo: {
                        color: resolveColor(
                            'text/on-feedback/info-subtle',
                            colorMode
                        ),
                    },
                    outlinedInfo: {
                        color: resolveColor(
                            'text/on-feedback/info-subtle',
                            colorMode
                        ),
                    },
                    ...(colorMode === 'dark' && {
                        // DS dark-mode filled alerts use LIGHT text + icons on
                        // the saturated severity background. MUI's default
                        // computes dark text (getContrastText of the light
                        // .main) => dark-on-dark, near-illegible. The filled
                        // icon inherits the root color, so this fixes both.
                        filled: ({ theme }) => ({
                            color: theme.palette.text.body,
                        }),
                    }),
                },
            },
            MuiTooltip: {
                defaultProps: {
                    /**
                     * When the tooltip wraps a button that becomes disabled
                     * there is a bug that leaves the tooltip open. Disabling the
                     * focus listener as a workaround
                     */
                    disableFocusListener: true,
                },
            },
            MuiTab: {
                styleOverrides: {
                    root: ({ ownerState, theme }) => ({
                        color: theme.palette.text.sub,
                        // Standard tabs use the DS tab height; stacked
                        // icon-above/below-label tabs keep MUI's 72px labelIcon
                        // sizing.
                        ...(!(
                            ownerState.icon &&
                            ownerState.label &&
                            (ownerState.iconPosition === 'top' ||
                                ownerState.iconPosition === 'bottom')
                        ) && {
                            minHeight: designTokens.DS_HEIGHT.TAB,
                        }),
                        ...(ownerState.icon &&
                            !ownerState.label && {
                                paddingTop: MUI_LABEL_ICON_PADDING_PX,
                                paddingBottom: MUI_LABEL_ICON_PADDING_PX,
                            }),
                    }),
                },
            },
            MuiTabs: {
                styleOverrides: {
                    root: {
                        // Match the tab height — MUI's 48px container default
                        // would otherwise stretch the tabs back to 48px.
                        minHeight: designTokens.DS_HEIGHT.TAB,
                    },
                },
            },
            MuiPaper: {
                defaultProps: {
                    elevation: 0,
                },
                styleOverrides: {
                    root: {
                        borderRadius: designTokens.PRIMITIVES.radius._12,
                        backgroundColor: resolveColor(
                            'surface/card',
                            colorMode
                        ),
                    },
                },
            },
            MuiMenu: {
                styleOverrides: {
                    // Menu/Select popups float over other content (and stack
                    // densely on small screens), so they need
                    // surface/card-solid's opaque fill rather than MuiPaper's
                    // default surface/card, which is translucent in dark mode
                    // (overlay/black/20) and reads as illegible/muddy over
                    // whatever sits behind the popup.
                    paper: {
                        backgroundColor: resolveColor(
                            'surface/card-solid',
                            colorMode
                        ),
                    },
                },
            },
            MuiCssBaseline: {
                styleOverrides: figtreeFontFaces
                    .map((fontFace) => createFontFace(fontFace))
                    .join('\n\n'),
            },
        },
        typography: {
            ...typographyVariants,

            fontFamily: FONT_FAMILY,
            htmlFontSize: designTokens.PRIMITIVES.typography.size._16, // Same as MUI default
            fontSize: designTokens.PRIMITIVES.typography.size._14, // Same as MUI default

            fontWeightBold: designTokens.PRIMITIVES.typography.weight._700,
            fontWeightMedium: designTokens.PRIMITIVES.typography.weight._500,
            fontWeightRegular: designTokens.PRIMITIVES.typography.weight._400,
            fontWeightLight: designTokens.DS_FONT_WEIGHT.LIGHT,
        },
        spacing: designTokens.PRIMITIVES.spacing._4,
        shape: {
            borderRadius: designTokens.PRIMITIVES.radius._4,
        },
        palette: colorMode === 'light' ? lightPalette : darkPalette,
        breakpoints: {
            values: {
                xs: designTokens.DS_BREAKPOINT.INTRA,
                sm: designTokens.DS_BREAKPOINT.SMALL,
                md: designTokens.DS_BREAKPOINT.MEDIUM,
                lg: designTokens.DS_BREAKPOINT.LARGE,
                xl: designTokens.DS_BREAKPOINT.ULTRA,
            },
        },
        zIndex: {
            mobileStepper: designTokens.DS_Z_INDEX.MOBILE_STEPPER,
            speedDial: designTokens.DS_Z_INDEX.SPEED_DIAL_AND_FAB,
            fab: designTokens.DS_Z_INDEX.SPEED_DIAL_AND_FAB,
            appBar: designTokens.DS_Z_INDEX.APP_BAR,
            drawer: designTokens.DS_Z_INDEX.DRAWER,
            audioSrControls: designTokens.DS_Z_INDEX.AUDIO_SR_CONTROLS,
            skipToMain: designTokens.DS_Z_INDEX.SKIP_TO_MAIN,
            mobilePromoDrawer: designTokens.DS_Z_INDEX.MOBILE_PROMO_DRAWER,
            modal: designTokens.DS_Z_INDEX.MODAL,
            mediaControls: designTokens.DS_Z_INDEX.MEDIA_CONTROLS,
            audioPlayerUpperActions:
                designTokens.DS_Z_INDEX.AUDIO_PLAYER_UPPER_ACTIONS,
            loadingDialog: designTokens.DS_Z_INDEX.LOADING_DIALOG,
            cookieDialog: designTokens.DS_Z_INDEX.COOKIE_DIALOG,
            snackbar: designTokens.DS_Z_INDEX.SNACKBAR,
            tooltip: designTokens.DS_Z_INDEX.TOOLTIP,
        },
    });
    // Attach after createTheme so the palette object is untouched — this keeps
    // the resolved-palette snapshot byte-identical (.semantics is a sibling of
    // .palette, not part of it).
    theme.semantics = buildSemantics(colorMode);
    theme.ds = DS;
    return theme;
};

export default makeTheme;
